import React, { useState } from "react";
import ClientRealLayout from "./Layout";
import ClientShiftsPageShell from "./ClientShiftsPageShell";
import usePageMeta from "../hooks/usePageMeta";
import { AtlasListToolbar } from "../components/list-toolbar";
import { AtlasFilterPanel, AtlasFilterChipBar, useFilterPanel } from "../components/filters";
import { ATLAS_BTN_PRIMARY, ATLAS_BTN_SECONDARY } from "../components/atlasButtonStyles";
import useClientSchedule from "./schedule/useClientSchedule";
import ClientScheduleStatusCards from "./schedule/ClientScheduleStatusCards";
import ClientScheduleGrid from "./schedule/ClientScheduleGrid";
import ClientScheduleNoteModal from "./schedule/ClientScheduleNoteModal";
import ClientScheduleFilterFields, {
  buildClientScheduleFilterChips,
} from "./schedule/clientScheduleFilterConfig";

const ClientShiftsSchedule = () => {
  usePageMeta("Shifts — Schedule");
  const filterPanel = useFilterPanel();
  const schedule = useClientSchedule();
  const [noteModal, setNoteModal] = useState(null);

  const chips = buildClientScheduleFilterChips({
    selectedClients: schedule.clientIds,
    clients: schedule.clients,
    selectedDate: schedule.dateFrom,
    selectedDateRange: schedule.dateRange,
    selectedCheckinStatuses: schedule.checkinStatuses,
  });

  return (
    <ClientRealLayout>
      <ClientShiftsPageShell bodyClassName={filterPanel.contentOffsetClass}>
        <div className="mt-3 flex-1 min-h-0 p-4 border-2 border-gray-200 rounded-xl flex flex-col min-w-0 bg-gray-50 overflow-hidden">
            <div className="flex-shrink-0">
              <AtlasListToolbar
                searchValue={schedule.searchQuery}
                onSearchChange={schedule.setSearchQuery}
                searchPlaceholder="Search clients, locations, staff, or shift ID"
                onFilterClick={filterPanel.toggle}
                filterOpen={filterPanel.isOpen}
                onRefresh={schedule.refresh}
                refreshSpinning={schedule.loading}
                showResultsMeta={false}
                actions={
                  <button
                    type="button"
                    onClick={schedule.toggleExpandAll}
                    className={
                      schedule.allExpanded
                        ? ATLAS_BTN_SECONDARY
                        : ATLAS_BTN_PRIMARY
                    }
                  >
                    {schedule.allExpanded ? "COLLAPSE ALL" : "EXPAND ALL"}
                  </button>
                }
                chipBar={
                  <AtlasFilterChipBar
                    chips={chips}
                    onChipClick={(chip) => {
                      if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                      else filterPanel.open();
                    }}
                    onRemove={schedule.removeFilterChip}
                    onClearAll={schedule.clearFilters}
                  />
                }
              />

              <ClientScheduleStatusCards stats={schedule.stats} />
            </div>

            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {schedule.needsClientSelection ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  Select a client in the filters to view the schedule.
                </div>
              ) : (
                <ClientScheduleGrid
                  scheduleDays={schedule.scheduleDays}
                  expandedCards={schedule.expandedCards}
                  onToggleCard={schedule.toggleCard}
                  onShowNote={setNoteModal}
                  loading={schedule.loading}
                  searchTrimmed={schedule.searchQuery.trim()}
                />
              )}
            </div>
          </div>
      </ClientShiftsPageShell>

      <ClientScheduleNoteModal
        modal={noteModal}
        onClose={() => setNoteModal(null)}
      />

      <AtlasFilterPanel
        isOpen={filterPanel.isOpen}
        onClose={filterPanel.close}
        onClear={schedule.clearFilters}
        activeCount={chips.length}
      >
        <ClientScheduleFilterFields
          focusedFieldId={filterPanel.focusedFieldId}
          onFocusClear={filterPanel.clearFocus}
          showClientFilter={schedule.isContactUser}
          clients={schedule.clients}
          selectedClients={schedule.clientIds}
          setSelectedClients={schedule.setClientIds}
          selectedCheckinStatuses={schedule.checkinStatuses}
          setSelectedCheckinStatuses={schedule.setCheckinStatuses}
          selectedDate={schedule.dateFrom}
          setSelectedDate={schedule.setDateFrom}
          selectedDateRange={schedule.dateRange}
          setSelectedDateRange={schedule.setDateRange}
        />
      </AtlasFilterPanel>
    </ClientRealLayout>
  );
};

export default ClientShiftsSchedule;
